import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  getAccountsGridTableData,
  getHistoricalBalances,
  getAccountTypesWithoutHidden,
  getColumnDefs,
  getColumnDefsRetirementOrder,
  getAccountsGridPrintLayout
} from 'state/account/AccountsGridSelectors';
import {
  getAccountPerformance,
  getNonPurgedAccounts
} from 'state/account/AccountSelectors';
import AccountTypeFilter from 'components/layouts/Account/AccountTypeFilter';
import {
  getDefaultGroupBy,
  getSelectedDefaultViewSettings
} from 'state/preferences/PreferencesSelectors';
import {
  isActiveAdvisory,
  isActivePlus,
  isBrokerage,
  isCollegeSavings,
  isMutualFund,
  isRetirementAccount
} from 'services/Products';
import {
  autoGroupColumnDef,
  autoGroupColumnDefMobile,
  defaultColumnDef,
  isRowGroup
} from 'components/layouts/Account/ColumnDefs';
import { PRINT_NONE } from 'services/PrintUtils';
import {
  AccountNameRenderer,
  DollarWithIndicator,
  PercentWithIndicator,
  PriceWithIndicator
} from 'services/TableUtils';
import PRRRenderer from 'services/PRRRenderer';
import { getSelections } from 'state/selections/SelectionsSelectors';

const AccountsGridDataProvider = (WrappedComponent) => {
  class AccountsGridData extends Component {
    // react components
    frameworkComponents = {
      accountTypeFilter: AccountTypeFilter,
      accountNameRenderer: AccountNameRenderer,
      dollarWithIndicator: DollarWithIndicator,
      priceWithIndicator: PriceWithIndicator,
      percentWithIndicator: PercentWithIndicator,
      prrRenderer: PRRRenderer
    };

    accountTypeOptions = {
      all: () => true,
      brokerage: isBrokerage,
      collegeSavings: isCollegeSavings,
      mutualFund: isMutualFund,
      activePlus: isActivePlus,
      activeAdvisory: isActiveAdvisory,
      retirement: isRetirementAccount,
      nonRetirement: (account) => !isRetirementAccount(account),
      owned: (account) => account.owner,
      related: (account) => account.isRelated,
      viewOnly: (account) => account.isViewOnly
    };

    gridOptions = {
      overlayNoRowsTemplate:
        '<span>No accounts to show. Please try different filtering options.</span>',
      domLayout: 'autoHeight',
      isGroupOpenByDefault: function (params) {
        return params.field !== 'groupId' && isRowGroup(params.field);
      },
      embedFullWidthRows: true,
      animateRows: true,
      applyColumnDefOrder: true,
      groupSuppressBlankHeader: true,
      suppressAggFuncInHeader: true,
      suppressRowTransform: true,
      enableRangeSelection: true,
      copyHeadersToClipboard: true,
      skipHeaderOnAutoSize: true,
      reactUi: true
    };

    render() {
      const {
        accounts,
        columnDefs,
        columnDefsRetirementOrder,
        defaultGroupBy,
        historicalBalances,
        accountTypesWithoutHidden,
        isDesktopLayout,
        isMobileLayout,
        transactionUrls,
        filters,
        rawAccounts,
        rawAccountsPerformance,
        selectedDefaultViewSettings,
        has529Only,
        printLayout,
        selections
      } = this.props;

      const isPrintLayout = printLayout !== PRINT_NONE;

      const data = {
        columnDefs,
        columnDefsRetirementOrder,
        defaultColumnDef,
        autoGroupColumnDef:
          isMobileLayout || isPrintLayout
            ? autoGroupColumnDefMobile
            : autoGroupColumnDef,
        components: this.components,
        frameworkComponents: this.frameworkComponents,
        accountTypeOptions: this.accountTypeOptions,
        gridOptions: this.gridOptions
      };

      const customAccountGroups =
        selectedDefaultViewSettings &&
        selectedDefaultViewSettings.customAccountGroups;

      return (
        <WrappedComponent
          accounts={accounts}
          customAccountGroups={customAccountGroups}
          defaultGroupBy={defaultGroupBy}
          historicalBalances={historicalBalances}
          accountTypesWithoutHidden={accountTypesWithoutHidden}
          isDesktopLayout={isDesktopLayout}
          isMobileLayout={isMobileLayout}
          transactionUrls={transactionUrls}
          filters={filters}
          rawAccounts={rawAccounts}
          rawAccountsPerformance={rawAccountsPerformance}
          has529Only={has529Only}
          printLayout={printLayout}
          selections={selections}
          {...data}
        />
      );
    }
  }

  const mapStateToProps = (state) => ({
    accounts: getAccountsGridTableData(state),
    columnDefs: getColumnDefs(state),
    columnDefsRetirementOrder: getColumnDefsRetirementOrder(state),
    defaultGroupBy: getDefaultGroupBy(state),
    historicalBalances: getHistoricalBalances(state),
    accountTypesWithoutHidden: getAccountTypesWithoutHidden(state),
    isDesktopLayout: state.view.isDesktopLayout,
    isMobileLayout: state.view.isMobileLayout,
    filters: state.accountsGridFilteringSelections,
    rawAccounts: getNonPurgedAccounts(state),
    rawAccountsPerformance: getAccountPerformance(state),
    selectedDefaultViewSettings: getSelectedDefaultViewSettings(state),
    has529Only: state.view.has529Only,
    printLayout: getAccountsGridPrintLayout(state),
    selections: getSelections(state)
  });

  return connect(mapStateToProps)(AccountsGridData);
};

export default AccountsGridDataProvider;
